import { useApiOutage } from '../../baseapi';
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { TrainFront, Landmark, Video, AlertTriangle, Activity } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { NODE_CHECKPOINTS, NODE_DOMINANT, NODE_EXIT_CHECKPOINTS, NODE_TOWARD_LABEL } from '../../config/flowNodes';

/**
 * 8-stage pilgrim flow board (entry gates → sanctum → dispersal), the "Flow" view of the old console.
 * Every figure is a real camera count from /api/summary; nodes without a camera say "NO CAMERA".
 */

interface Metric { m1: number; m5?: number; m15?: number; total: number }
interface Checkpoint {
  id: string;
  name: string;
  coverage?: string;
  cameras: { id: string; state: string }[];
  people?: { toward?: Metric; away?: Metric };
  [k: string]: any;
}
interface Summary {
  headline?: Record<string, Metric | undefined>;
  checkpoints?: Record<string, Checkpoint>;
}

interface NodeLive {
  checkpoints: Checkpoint[];
  cams: number;
  live: boolean;
  rate: number;   // people per minute, last minute, "toward"
  total: number;  // since 00:00, "toward"
  away: number;   // since 00:00, opposite direction
  out: number | null;   // since 00:00, through the node's exit camera (NODE_EXIT_CHECKPOINTS); null = none
  replay?: string;      // e.g. "Replay · 13 Oct": the cameras are down, the figures are yesterday's (GET /api/summary/replay)
}

const NW = 188;
const NH = 66;
const VB_W = 1800;   // 16 Oct 2026: 7 columns 260 apart (room for the travel times between them)
const VB_H = 560;

// Route (16 Oct 2026): station gates -> Gadde Bomma -> Vinayaka junction -> holding area -> Vinayaka Q line -> temple;
// bus station -> Rajiv Gandhi Park -> holding area; Kummaripalem & ghats -> Kummaripalem Q line -> temple;
// one exit from the temple for everybody.
// (Boddu Bomma and the VIP entrance are no longer part of the pilgrim route.)
const ORDER = ["STN-MAIN", "STN-THARA", "STN-ET2", "STN-PARCEL", "PNBS", "LOCAL",
  "GADDE", "VINAYAKA", "RGPARK", "HOLDING", "QLINE", "QLINE-K", "TEMPLE", "EXIT-KDN"];   // Kanaka Durga Nagar removed 16 Oct 2026

const FLOW_POS: Record<string, [number, number]> = {
  "STN-MAIN": [120, 92], "STN-THARA": [120, 168], "STN-ET2": [120, 245], "STN-PARCEL": [120, 321],
  "PNBS": [120, 398], "LOCAL": [120, 474],
  "GADDE": [380, 207], "VINAYAKA": [640, 237], "RGPARK": [640, 398], "HOLDING": [900, 317],
  "QLINE": [1160, 352], "QLINE-K": [1160, 474], "TEMPLE": [1420, 352],
  "EXIT-KDN": [1680, 352],
};

const FLOW_COLHDR: [number, string][] = [
  [120, "1. Entry Gates"], [380, "2. Feeder Merge"], [640, "3. Junction & Park"], [900, "4. Holding Area"],
  [1160, "5. Queue Lines"], [1420, "6. Sanctum"], [1680, "7. Clearance Exit"],
];

const SHORT_NAMES: Record<string, string> = {
  "STN-MAIN": "Station main gate", "STN-THARA": "Tharapeta gate", "STN-ET2": "East Terminal 2",
  "STN-PARCEL": "Parcel office gate", "PNBS": "Bus station", "LOCAL": "Kummaripalem & ghats",
  "GADDE": "Gadde Bomma", "VINAYAKA": "Vinayaka junction", "RGPARK": "Rajiv Gandhi Park",
  "HOLDING": "Holding area", "QLINE": "Vinayaka Q line", "QLINE-K": "Kummaripalem Q line", "TEMPLE": "Temple",
  "EXIT-KDN": "Main exit",
};

const LINKS: [string, string][] = [
  ["STN-MAIN", "GADDE"], ["STN-THARA", "GADDE"], ["STN-ET2", "GADDE"], ["STN-PARCEL", "GADDE"],
  ["GADDE", "VINAYAKA"], ["VINAYAKA", "HOLDING"], ["PNBS", "RGPARK"], ["RGPARK", "HOLDING"],
  // 16 Oct 2026, operators: Kummaripalem & ghats -> their own Q line (192.168.2.47) -> temple; Vinayaka / holding
  // -> the Vinayaka Q line (192.168.2.102) -> temple; one exit from the temple for both
  ["HOLDING", "QLINE"], ["LOCAL", "QLINE-K"], ["QLINE", "TEMPLE"], ["QLINE-K", "TEMPLE"],
  ["TEMPLE", "EXIT-KDN"],   // no Vinayaka junction -> Q line: everybody goes through the holding area (16 Oct 2026)
];

const fmt = (n: number) => Math.round(n).toLocaleString('en-IN');

function kindStyle(id: string, isLight: boolean) {
  const L = (label: string, color: string, border: string, bg: string) => ({ label, color, border, bg });
  if (isLight) {
    if (id.startsWith('STN')) return L('RAIL GATE', '#059669', '#A7F3D0', '#ECFDF5');
    if (id === 'PNBS') return L('BUS TERMINAL', '#D97706', '#FDE68A', '#FFFBEB');
    if (id === 'LOCAL') return L('GHATS ENTRY', '#0284C7', '#BAE6FD', '#F0F9FF');
    if (['GADDE', 'VINAYAKA'].includes(id)) return L('JUNCTION', '#16A34A', '#BBF7D0', '#F0FDF4');
    if (id === 'RGPARK') return L('PARK', '#0D9488', '#99F6E4', '#F0FDFA');
    if (id === 'HOLDING') return L('HOLDING AREA', '#B45309', '#FDE68A', '#FEFCE8');
    if (id.startsWith('QLINE')) return L('QUEUE LINES', '#7C3AED', '#E9D5FF', '#FAF5FF');
    if (id === 'TEMPLE') return L('SANCTUM CORE', '#C2410C', '#FED7AA', '#FFF7ED');
    return L('DISPERSAL', '#E11D48', '#FECDD3', '#FFF1F2');
  }
  if (id.startsWith('STN')) return L('RAIL GATE', '#00F0B5', '#164E3E', '#071A16');
  if (id === 'PNBS') return L('BUS TERMINAL', '#FFB347', '#4E3716', '#1A1407');
  if (id === 'LOCAL') return L('GHATS ENTRY', '#7E9EB5', '#1B3D56', '#081724');
  if (['GADDE', 'VINAYAKA'].includes(id)) return L('JUNCTION', '#38D9A9', '#1A4D66', '#081B28');
  if (id === 'RGPARK') return L('PARK', '#2DD4BF', '#134E4A', '#07201E');
  if (id === 'HOLDING') return L('HOLDING AREA', '#FFD43B', '#5C4516', '#1C1508');
  if (id.startsWith('QLINE')) return L('QUEUE LINES', '#B197FC', '#3E2F68', '#130E24');
  if (id === 'TEMPLE') return L('SANCTUM CORE', '#FFD43B', '#73531E', '#1F1507');
  return L('DISPERSAL', '#FF7A85', '#5A1F26', '#1C0B0E');
}

/** Time from one stage to the next, shown on its link (16 Oct 2026, operators). "queue" = measured now: the people in
 *  the queue zones on the way / the rate each zone empties; "walk" = an estimate from the map distance. */
export interface LinkTime { min: number | null; kind: 'queue' | 'walk'; title?: string }

export const NetworkFlowBoard: React.FC<{
  summary: Summary | null;
  live: boolean;                                   // false = API not reachable (summary is not live data)
  /** Open the camera popup for a node; nodes counted by several checkpoints pass them all (camera tabs). */
  onOpenNode: (checkpoints: Checkpoint[]) => void;
  /** key "FROM>TO" (node ids) */
  linkTimes?: Record<string, LinkTime>;
}> = ({ summary, live, onOpenNode, linkTimes }) => {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const muted = isLight ? '#64748B' : '#6F95B0';

  const byNode = useMemo(() => {
    const out: Record<string, NodeLive> = {};
    ORDER.forEach(id => {
      const find = (ids: string[]) => ids.map(c => summary?.checkpoints?.[c]).filter((cp): cp is Checkpoint => !!cp);
      const cps = find(NODE_CHECKPOINTS[id] || []);
      const exits = find(NODE_EXIT_CHECKPOINTS[id] || []);
      // temple queue nodes: count each checkpoint in its busier direction (see NODE_DOMINANT); an exit camera too
      const busier = (cp: Checkpoint): 'toward' | 'away' =>
        (cp.people?.away?.total || 0) > (cp.people?.toward?.total || 0) ? 'away' : 'toward';
      const main = (cp: Checkpoint): 'toward' | 'away' => (NODE_DOMINANT.has(id) ? busier(cp) : 'toward');
      const all = [...cps, ...exits];
      const cams = all.reduce((a, cp) => a + (cp.cameras || []).filter(c => c.state !== 'no_camera').length, 0);
      out[id] = {
        checkpoints: all.filter(cp => (cp.cameras || []).length > 0),
        cams,
        live: all.some(cp => cp.coverage === 'live'),
        rate: cps.reduce((a, cp) => a + (cp.people?.[main(cp)]?.m1 || 0), 0),
        total: cps.reduce((a, cp) => a + (cp.people?.[main(cp)]?.total || 0), 0),
        away: cps.reduce((a, cp) => a + (cp.people?.[main(cp) === 'toward' ? 'away' : 'toward']?.total || 0), 0),
        out: exits.length ? exits.reduce((a, cp) => a + (cp.people?.[busier(cp)]?.total || 0), 0) : null,
        replay: (all.find(cp => cp.replay)?.replay as string | undefined),
      };
    });
    return out;
  }, [summary]);

  const h = summary?.headline;
  const leavingNow = h ? (h.rail_out?.m1 || 0) + (h.bus_out?.m1 || 0) + (h.kummari_toward?.m1 || 0) : null;
  const leavingToday = h ? (h.rail_out?.total || 0) + (h.bus_out?.total || 0) + (h.kummari_toward?.total || 0) : null;
  // only meaningful once the temple approach (final checkpoint) has a camera
  const templeCounted = (byNode['TEMPLE']?.cams || 0) > 0;
  const arrivingNow = templeCounted ? byNode['TEMPLE'].rate : null;
  const arrivingToday = templeCounted ? byNode['TEMPLE'].total : null;
  const outage = useApiOutage();     // true only after 2 minutes without an answer: a restart shows no message
  const camStats = useMemo(() => {
    let on = 0, down = 0;
    Object.values(summary?.checkpoints || {}).forEach(cp => (cp.cameras || []).forEach(c => {
      if (c.state === 'online') on++; else if (c.state !== 'no_camera' && c.state !== 'standby') down++;   // standby = on demand, reachable
    }));
    return { on, down };
  }, [summary]);

  const openNode = (id: string) => {
    const n = byNode[id];
    if (!live || !n || n.checkpoints.length === 0) return;
    onOpenNode(n.checkpoints);
  };

  const boardRef = useRef<HTMLDivElement>(null);
  const [boardH, setBoardH] = useState(VB_H);
  const [boardW, setBoardW] = useState(1860);

  useEffect(() => {
    const el = boardRef.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setBoardW(rect.width);
        setBoardH(rect.height);
      }
    };
    update();
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setBoardW(width);
          setBoardH(height);
        }
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const vbH = useMemo(() => {
    const effectiveW = Math.max(1100, boardW || 1860);
    const effectiveH = boardH || VB_H;
    const calculated = Math.round(VB_W * (effectiveH / effectiveW));
    return Math.max(VB_H, calculated);
  }, [boardW, boardH]);

  const flowPos = useMemo<Record<string, [number, number]>>(() => {

    const topY = 90;                 // first card top at y 57, clear of the column headers (y 8-36)
    const bottomY = vbH - 95;

    const col1Step = (bottomY - topY) / 5;

    const stnMain = topY;
    const stnThara = topY + col1Step;
    const stnEt2 = topY + col1Step * 2;
    const stnParcel = topY + col1Step * 3;
    const pnbs = topY + col1Step * 4;
    const local = bottomY;

    const gadde = (stnMain + stnParcel) / 2;
    const vinayaka = gadde + Math.min(45, (stnParcel - stnEt2) * 0.4);
    const rgpark = pnbs;
    const holding = (vinayaka + rgpark) / 2;

    const qline = holding + (local - holding) * 0.22;
    const temple = qline;
    const exitKdn = qline;

    return {
      "STN-MAIN": [120, Math.round(stnMain)],
      "STN-THARA": [120, Math.round(stnThara)],
      "STN-ET2": [120, Math.round(stnEt2)],
      "STN-PARCEL": [120, Math.round(stnParcel)],
      "PNBS": [120, Math.round(pnbs)],
      "LOCAL": [120, Math.round(local)],
      "GADDE": [380, Math.round(gadde)],
      "VINAYAKA": [640, Math.round(vinayaka)],
      "RGPARK": [640, Math.round(rgpark)],
      "HOLDING": [900, Math.round(holding)],
      "QLINE": [1160, Math.round(qline)],
      "QLINE-K": [1160, Math.round(local)],          // level with Kummaripalem & ghats: a straight link
      "TEMPLE": [1420, Math.round(temple)],
      "EXIT-KDN": [1680, Math.round(exitKdn)],
    };
  }, [vbH]);

  const pos = (id: string) => flowPos[id] || FLOW_POS[id] || [120, 70];

  return (
    <div className="relative w-full rounded-xl overflow-hidden border border-neutral-200 dark:border-[#1b2a38] bg-white dark:bg-[#050b12] flex-1 flex flex-col min-h-0 shadow-xs">
      {/* heading */}
      <div className="shrink-0 px-4 pt-2.5 pb-2 border-b border-neutral-200 dark:border-[#12202c] flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <div className="flex items-baseline gap-2.5 flex-wrap">
          <h2 className="text-sm sm:text-base font-black tracking-tight text-neutral-900 dark:text-neutral-100">Pilgrim Route Flow</h2>
          <span className="text-xs sm:text-[13px] font-bold text-emerald-600 dark:text-[#38D9A9]">యాత్రికుల మార్గ ప్రవాహం</span>
        </div>
        <span className="text-xs sm:text-[12.5px] font-medium text-neutral-500 dark:text-[#6F95B0] flex flex-wrap items-center gap-x-3">
          <span>7 stages · entry gates → sanctum → exit · live camera counts</span>
          <span className="inline-flex items-center gap-1"><span className="px-1.5 rounded-full border border-violet-500 text-violet-700 dark:text-violet-300 text-[11px] font-bold">⏱</span>time in queue (live)</span>
          <span className="inline-flex items-center gap-1"><span className="px-1.5 rounded-full border border-dashed border-slate-400 text-slate-600 dark:text-slate-300 text-[11px] font-bold">⏱</span>walking time (estimate)</span>
        </span>
      </div>

      {/* KPI strip */}
      <div className="shrink-0 px-4 py-2 border-b border-neutral-200 dark:border-[#12202c] flex flex-wrap items-center justify-between gap-2 text-xs sm:text-[13px] font-mono bg-neutral-50 dark:bg-[#07111a]">
        <div className="flex flex-wrap items-center gap-5">
          <span className="flex items-center gap-2 text-neutral-600 dark:text-[#8FAEC5] font-medium">
            <TrainFront className="w-4 h-4 text-emerald-500" />
            Leaving station · bus stand · Kummaripalem:
            <b className="text-emerald-600 dark:text-[#00F0B5] text-sm sm:text-base font-black">{live && leavingNow !== null ? `${fmt(leavingNow)}/min` : '—'}</b>
            <span className="text-[10.5px] sm:text-[11.5px] text-neutral-400 dark:text-[#5d7a90] font-normal">{live && leavingToday !== null ? `${fmt(leavingToday)} since 00:00` : ''}</span>
          </span>
          <span className="flex items-center gap-2 text-neutral-600 dark:text-[#8FAEC5] font-medium">
            <Landmark className="w-4 h-4 text-amber-500" />
            Through temple queue lines:
            <b className="text-amber-600 dark:text-[#FFD43B] text-sm sm:text-base font-black">{live && arrivingNow !== null ? `${fmt(arrivingNow)}/min` : '—'}</b>
            <span className="text-[10.5px] sm:text-[11.5px] text-neutral-400 dark:text-[#5d7a90] font-normal">{live && arrivingToday ? `${fmt(arrivingToday)} since 00:00` : 'no camera'}</span>
          </span>
        </div>
        <div className="flex items-center gap-3">
          {!live && !outage ? null : !live ? (
            <span className="flex items-center gap-1.5 text-rose-500 bg-rose-500/15 px-2.5 py-1 rounded border border-rose-500/40 text-xs sm:text-[13px] font-bold">
              <AlertTriangle className="w-3.5 h-3.5" /> Camera analytics not reachable
            </span>
          ) : (
            <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded border text-xs sm:text-[13px] font-bold ${camStats.down ? 'text-amber-500 bg-amber-500/15 border-amber-500/40' : 'text-emerald-500 bg-emerald-500/15 border-emerald-500/40'}`}>
              <Video className="w-3.5 h-3.5" /> {camStats.on} cameras live{camStats.down ? ` · ${camStats.down} no video` : ''}
            </span>
          )}
          <span className="hidden xl:flex items-center gap-1 text-[11px] sm:text-xs text-neutral-400 dark:text-[#5d7a90]"><Activity className="w-3 h-3" />click a node for its cameras</span>
        </div>
      </div>

      {/* Board */}
      <div ref={boardRef} className="w-full flex-1 min-h-[420px] overflow-x-auto p-2 sm:p-3 flex flex-col justify-center">
        <div className="min-w-[1100px] w-full flex-1 flex items-center justify-center">
          <svg viewBox={`0 0 ${VB_W} ${vbH}`} preserveAspectRatio="xMidYMid meet" className="w-full h-full block select-none">
            <defs>
              <pattern id="nfbGrid" width="40" height="40" patternUnits="userSpaceOnUse">
                <circle cx="2" cy="2" r="1" fill={isLight ? 'rgba(148,163,184,0.4)' : 'rgba(32,64,88,0.22)'} />
              </pattern>
              <filter id="nfbShadow" x="-10%" y="-10%" width="120%" height="130%">
                <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="#000" floodOpacity={isLight ? 0.08 : 0.55} />
              </filter>
            </defs>
            <rect width={VB_W} height={vbH} fill="url(#nfbGrid)" />

            {FLOW_COLHDR.map(([x, title]) => (
              <g key={title}>
                <line x1={x} y1={38} x2={x} y2={vbH - 45} stroke={isLight ? 'rgba(203,213,225,0.8)' : 'rgba(24,48,68,0.35)'} strokeWidth="1" strokeDasharray="4 6" />
                <rect x={x - 80} y={8} width={160} height={28} rx="3" fill={isLight ? '#FFFFFF' : '#071522'} stroke={isLight ? '#CBD5E1' : '#16334A'} />
                <text x={x} y={26} textAnchor="middle" fill={isLight ? '#334155' : '#8FAEC5'} fontSize="12.5" fontWeight="800" letterSpacing="0.06em">{title.toUpperCase()}</text>
              </g>
            ))}

            {/* links: green = fed by a live camera count, grey = no camera at the source */}
            {LINKS.map(([from, to]) => {
              const a = pos(from), b = pos(to);
              const x1 = a[0] + NW / 2, y1 = a[1], x2 = b[0] - NW / 2, y2 = b[1];

              let d: string;
              let mid: [number, number] = [(x1 + x2) / 2, (y1 + y2) / 2];   // where the travel time is written
              if (from === 'LOCAL' && to === 'QLINE-K') {
                d = `M${x1},${y1} L${x2},${y2}`;           // the bottom row, under the other columns' cards
              } else {
                const c = Math.max(40, Math.abs(x2 - x1) * 0.45);
                d = `M${x1},${y1} C${x1 + c},${y1} ${x2 - c},${y2} ${x2},${y2}`;
              }

              const fed = live && byNode[from]?.live;
              const col = fed ? (isLight ? '#059669' : '#00F0B5') : (isLight ? '#94A3B8' : '#3A4E64');
              const lt = linkTimes?.[`${from}>${to}`];
              const ltText = lt && lt.min != null ? `${lt.min >= 60 ? `${Math.floor(lt.min / 60)} h ${Math.round(lt.min % 60)} m` : `${Math.max(1, Math.round(lt.min))} min`}` : null;
              return (
                <g key={`${from}-${to}`}>
                  <path d={d} fill="none" stroke={isLight ? 'rgba(226,232,240,0.9)' : 'rgba(10,26,38,0.6)'} strokeWidth={3} strokeLinecap="round" />
                  <path className="flow-base-dashed" d={d} fill="none" stroke={col} strokeWidth={2.4} style={{ opacity: 0.55 }} />
                  {fed && <path className="flow-highlight-pulse" d={d} fill="none" stroke={col} strokeWidth={4.5} style={{ filter: isLight ? 'none' : `drop-shadow(0 0 5px ${col})` }} />}
                  {ltText && (() => {
                    const q = lt!.kind === 'queue';
                    const label = `⏱ ${ltText}`;
                    const w = Math.min(70, 12 + label.length * 6.0);   // fits the 72 between two cards
                    return (
                      <g transform={`translate(${mid[0]},${mid[1]})`}>
                        <title>{lt!.title ?? ''}</title>
                        <rect x={-w / 2} y={-10} width={w} height={20} rx={10}
                          fill={q ? (isLight ? '#F5F3FF' : '#1A1233') : (isLight ? '#FFFFFF' : '#0B1622')}
                          stroke={q ? (isLight ? '#7C3AED' : '#B197FC') : (isLight ? '#94A3B8' : '#3A4E64')}
                          strokeWidth={1.3} strokeDasharray={q ? undefined : '3 2'} />
                        <text x={0} y={4} textAnchor="middle" fontSize="11" fontWeight="800"
                          fill={q ? (isLight ? '#5B21B6' : '#D0BFFF') : (isLight ? '#334155' : '#B8CCDA')}>{label}</text>
                      </g>
                    );
                  })()}
                </g>
              );
            })}

            {/* node cards */}
            {ORDER.map(id => {
              const [cx, cy] = pos(id);
              const s = kindStyle(id, isLight);
              const n = byNode[id];
              const x = cx - NW / 2, y = cy - NH / 2;
              const hasCam = live && n.cams > 0;
              const clickable = hasCam && n.checkpoints.length > 0;
              // a camera taken away during the day leaves its counts behind: they stay in the top cards, so the node
              // keeps showing them too (marked) instead of dropping to "no camera" and making the two disagree
              const counted = live && !hasCam && (n.total > 0 || n.away > 0);
              return (
                <g key={id} filter="url(#nfbShadow)" className={clickable ? 'cursor-pointer' : 'cursor-default'} onClick={() => openNode(id)} opacity={hasCam ? 1 : counted ? 0.85 : 0.62}>
                  <title>{hasCam ? `${n.cams} camera(s) · ${n.checkpoints.map(c => c.name).join(', ')}` : counted ? 'Counted earlier today; the camera is no longer on this node' : 'No camera mapped to this node yet'}</title>
                  <rect x={x} y={y} width={NW} height={NH} rx="4" fill={s.bg} stroke={s.border} strokeWidth="1.4" />
                  <circle cx={x} cy={cy} r="3.5" fill={isLight ? '#FFF' : '#03080E'} stroke={s.color} strokeWidth="1.5" />
                  <circle cx={x + NW} cy={cy} r="3.5" fill={isLight ? '#FFF' : '#03080E'} stroke={s.color} strokeWidth="1.5" />
                  <rect x={x + 1} y={y + 1} width={NW - 2} height={18} rx="3" fill={isLight ? 'rgba(0,0,0,0.03)' : 'rgba(0,0,0,0.35)'} />
                  <text x={x + 8} y={y + 13} fill={s.color} fontSize="10" fontWeight="700" letterSpacing="0.08em">{s.label}</text>
                  <text x={x + NW - 8} y={y + 13} textAnchor="end" fill={n.replay && !n.live ? '#F59E0B' : muted} fontSize="10" fontWeight={n.replay && !n.live ? '800' : '600'}>
                    {hasCam ? (n.replay && !n.live ? n.replay.toUpperCase() : `${n.cams} CAM${n.cams > 1 ? 'S' : ''} · ${n.live ? 'LIVE' : 'NO VIDEO'}`) : counted ? 'NO CAMERA NOW' : 'NO CAMERA'}
                  </text>
                  <text x={x + 8} y={y + 34} fill={isLight ? '#0F172A' : '#F0F6FC'} fontSize="14.5" fontWeight="800">{SHORT_NAMES[id]}</text>
                  <text x={x + 8} y={y + 54} fill={s.color} fontSize="16" fontWeight="800">
                    {hasCam ? fmt(n.rate) : '—'}
                    <tspan fill={muted} fontSize="10.5" fontWeight="normal">{hasCam ? ` /m ${NODE_TOWARD_LABEL[id] || 'to temple'}` : counted ? ' earlier today' : ' no camera'}</tspan>
                  </text>
                  {/* (the "N Out today" figure was removed on 16 Oct 2026 - operators) */}
                </g>
              );
            })}

            {/* legend */}
            <g transform={`translate(52, ${vbH - 24})`}>
              <rect x="0" y="-15" width="800" height="30" rx="3" fill={isLight ? '#FFFFFF' : '#06131F'} stroke={isLight ? '#CBD5E1' : '#142B3E'} />
              <text x="14" y="5" fill={isLight ? '#475569' : '#8FAEC5'} fontSize="12.5">
                <tspan fill={isLight ? '#059669' : '#00F0B5'} fontWeight="bold">● GREEN:</tspan> link fed by a live camera count
                <tspan dx="16" fill={muted} fontWeight="bold">● GREY:</tspan> no camera at the source node
                <tspan dx="16" fill={muted}>CARD: people/min now</tspan>
              </text>
            </g>
          </svg>
        </div>
      </div>

    </div>
  );
};

export default NetworkFlowBoard;
